import { type ReactNode, useId, useRef, useState } from 'react';

import { ChevronDown, CircleX } from 'lucide-react';

import { useBreakpointDown } from '@nesso/ui/hooks/use-breakpoint-down';
import { Button } from '@nesso/ui/primitives/button';
import { DialogTrigger } from '@nesso/ui/primitives/dialog';
import { Label } from '@nesso/ui/primitives/label';
import { PopoverTrigger } from '@nesso/ui/primitives/popover';

import { MultiSelectDialog } from './multi-select/multi-select-dialog';
import { MultiSelectField } from './multi-select/multi-select-field';
import type { MultiSelectOption } from './multi-select/multi-select-option';
import { MultiSelectPopover } from './multi-select/multi-select-popover';
import { MultiSelectTriggerContent } from './multi-select/multi-select-trigger-content';

const DEFAULT_CHEVRON_ICON = (
    <ChevronDown className="text-muted-foreground/80 shrink-0" aria-hidden="true" />
);
const DEFAULT_DELETE_ICON = <CircleX className="w-4 h-4" />;

type Props = {
    label?: string;
    options: MultiSelectOption[];
    selected: string[];
    onChange: (selected: string[]) => void;
    placeholder?: string;
    maxSelected?: number;
    searchPlaceholder?: string;
    groupLabel?: string;
    groupOrder?: string[];
    disabled?: boolean;
    description?: string;
    icon?: ReactNode;
    className?: string;
    badgeClassName?: string;
    notFoundMessage?: string;
    deleteBadgeIcon?: ReactNode;
    loading?: boolean;
    error?: boolean;
    /** Below `xs`: `'mobile-popover'` (default) keeps the popover; `'mobile-dialog'` opens fullscreen. */
    variant?: 'mobile-popover' | 'mobile-dialog';
};

export function MultiSelect({
    label,
    options,
    selected,
    onChange,
    placeholder,
    maxSelected,
    searchPlaceholder,
    groupLabel,
    groupOrder,
    disabled,
    description,
    icon = DEFAULT_CHEVRON_ICON,
    className,
    badgeClassName,
    deleteBadgeIcon = DEFAULT_DELETE_ICON,
    notFoundMessage = 'No options found.',
    loading = false,
    error = false,
    variant = 'mobile-popover',
}: Props) {
    const id = useId();
    const [open, setOpen] = useState(false);
    const isXs = useBreakpointDown('xs');
    const fieldRef = useRef<HTMLDivElement | null>(null);

    const showDialog = isXs && variant === 'mobile-dialog';

    // Falls back through the same chain the dialog title already used. The combobox needs its
    // own accessible name too: none of the real call sites pass `label`, so without this the
    // button would have no name at all — its only visible content is an aria-hidden chevron.
    const fallbackName = groupLabel ?? searchPlaceholder ?? 'Select options';
    const dialogTitle = label ?? fallbackName;
    // When `label` is set, `<Label htmlFor={id}>` already supplies the accessible name via
    // association — an aria-label here would silently override it (aria-label wins over a
    // referenced label element), so only fall back to it when there's no `label`.
    const comboboxAriaLabel = label ? undefined : fallbackName;
    const selectedContentId = `${id}-selected`;

    const handleRemove = (value: string) => {
        onChange(selected.filter((v) => v !== value));
    };

    const comboboxButton = (
        <Button
            id={id}
            variant="ghost"
            size="icon-sm"
            role="combobox"
            aria-expanded={open}
            aria-invalid={error || undefined}
            aria-label={comboboxAriaLabel}
            // The chips that used to sit inside this button now live outside it — describe the
            // current selection so it's still announced when the combobox receives focus.
            aria-describedby={selectedContentId}
            disabled={disabled}
            className="shrink-0 hover:bg-transparent focus-visible:ring-0"
            onMouseDown={(e) => {
                if (disabled) {
                    e.preventDefault();
                    return;
                }
                if (open) e.preventDefault();
            }}
            onClick={() => {
                if (disabled) return;
                setOpen(true);
            }}
        >
            {icon}
        </Button>
    );

    const field = (
        <MultiSelectField
            ref={fieldRef}
            className={className}
            disabled={disabled}
            contentId={selectedContentId}
            onClick={() => {
                // Only ever opens. The chevron inside the field is the real trigger and toggles
                // on its own, so forcing `true` here would undo the close it just performed.
                if (!disabled && !open) setOpen(true);
            }}
            fieldContent={
                <MultiSelectTriggerContent
                    options={options}
                    selected={selected}
                    placeholder={placeholder}
                    groupLabel={groupLabel}
                    deleteBadgeIcon={deleteBadgeIcon}
                    badgeClassName={badgeClassName}
                    disabled={disabled}
                    onRemove={handleRemove}
                />
            }
            triggerButton={
                showDialog ? (
                    <DialogTrigger asChild>{comboboxButton}</DialogTrigger>
                ) : (
                    <PopoverTrigger asChild>{comboboxButton}</PopoverTrigger>
                )
            }
        />
    );

    return (
        <div className="space-y-2">
            {label && <Label htmlFor={id}>{label}</Label>}
            {showDialog ? (
                <MultiSelectDialog
                    open={open}
                    onOpenChange={setOpen}
                    title={dialogTitle}
                    options={options}
                    selected={selected}
                    onChange={onChange}
                    maxSelected={maxSelected}
                    groupOrder={groupOrder}
                    searchPlaceholder={searchPlaceholder}
                    notFoundMessage={notFoundMessage}
                    deleteBadgeIcon={deleteBadgeIcon}
                    disabled={disabled}
                    loading={loading}
                    badgeClassName={badgeClassName}
                >
                    {field}
                </MultiSelectDialog>
            ) : (
                <MultiSelectPopover
                    open={open}
                    onOpenChange={setOpen}
                    fieldRef={fieldRef}
                    options={options}
                    selected={selected}
                    onChange={onChange}
                    maxSelected={maxSelected}
                    groupOrder={groupOrder}
                    searchPlaceholder={searchPlaceholder}
                    notFoundMessage={notFoundMessage}
                    loading={loading}
                >
                    {field}
                </MultiSelectPopover>
            )}
            {description && <p className="text-muted-foreground text-sm">{description}</p>}
        </div>
    );
}
